/**
 * Pure release-readiness gate for Eagle capital materials.
 * Client-side helper only — server approval re-validates independently.
 */

export const REQUIRED_RELEASE_CATEGORIES = [
  'concentration',
  'title_midstream',
  'aro_pna',
  'overhead_treatment',
  'operating_continuity',
];

export const RELEASE_CLASSIFICATIONS = new Set(['fact', 'model', 'judgment']);
export const RELEASE_DISPOSITIONS = new Set(['cleared', 'disclosed', 'block']);

const LOGICAL_SOURCE_RE = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,159}$/;
const ABSOLUTE_PATH_RE = /(^\/|^[A-Za-z]:[\\/]|\\\\|\/Users\/|\/opt\/)/i;
const TREATMENT_CATEGORIES = new Set(['aro_pna', 'overhead_treatment']);

function text(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function defaultItem(category, asOf = '') {
  const item = {
    category,
    sourceRef: '',
    asOf: asOf || '',
    owner: '',
    classification: category === 'concentration' ? 'model' : 'judgment',
    disposition: 'block',
    summary: '',
  };
  if (TREATMENT_CATEGORIES.has(category)) item.treatment = '';
  return item;
}

export function createReleaseReadiness({ asOf = '' } = {}) {
  return {
    schemaVersion: 'release-readiness.v1',
    items: REQUIRED_RELEASE_CATEGORIES.map((category) => defaultItem(category, asOf)),
  };
}

function containsAbsolutePath(value) {
  if (typeof value === 'string') return ABSOLUTE_PATH_RE.test(value);
  if (Array.isArray(value)) return value.some(containsAbsolutePath);
  if (value && typeof value === 'object') {
    return Object.values(value).some(containsAbsolutePath);
  }
  return false;
}

function itemIssues(raw) {
  const issues = [];
  if (!raw || typeof raw !== 'object') {
    return ['missing'];
  }
  const sourceRef = text(raw.sourceRef);
  const asOf = text(raw.asOf);
  const owner = text(raw.owner);
  const summary = text(raw.summary);
  const classification = raw.classification;
  const disposition = raw.disposition;
  if (!sourceRef) issues.push('sourceRef');
  else if (!LOGICAL_SOURCE_RE.test(sourceRef)) issues.push('sourceRef_format');
  if (!asOf) issues.push('asOf');
  if (!owner) issues.push('owner');
  if (!summary) issues.push('summary');
  if (!RELEASE_CLASSIFICATIONS.has(classification)) issues.push('classification');
  if (!RELEASE_DISPOSITIONS.has(disposition)) issues.push('disposition');
  if (TREATMENT_CATEGORIES.has(raw.category)) {
    if (!text(raw.treatment)) issues.push('treatment');
  }
  if (containsAbsolutePath(summary) || containsAbsolutePath(raw.treatment)) {
    issues.push('absolute_path');
  }
  return issues;
}

function normalizeItem(category, raw, fallbackAsOf = '') {
  const base = defaultItem(category, fallbackAsOf);
  if (!raw || typeof raw !== 'object') return base;
  const item = {
    ...base,
    sourceRef: text(raw.sourceRef),
    asOf: text(raw.asOf) || fallbackAsOf || '',
    owner: text(raw.owner),
    classification: RELEASE_CLASSIFICATIONS.has(raw.classification)
      ? raw.classification
      : base.classification,
    disposition: RELEASE_DISPOSITIONS.has(raw.disposition) ? raw.disposition : 'block',
    summary: text(raw.summary),
  };
  if (TREATMENT_CATEGORIES.has(category)) {
    item.treatment = text(raw.treatment);
  }
  return item;
}

/**
 * Evaluate the versioned release-readiness contract.
 * Incomplete metadata is treated as a blocker (never silently cleared).
 */
export function evaluateReleaseReadiness(readiness, { asOf = '' } = {}) {
  const byCategory = new Map();
  const rawItems = Array.isArray(readiness?.items) ? readiness.items : [];
  for (const raw of rawItems) {
    if (raw && REQUIRED_RELEASE_CATEGORIES.includes(raw.category)) {
      byCategory.set(raw.category, raw);
    }
  }

  const items = REQUIRED_RELEASE_CATEGORIES.map((category) =>
    normalizeItem(category, byCategory.get(category), asOf),
  );

  const blockers = [];
  const disclosures = [];
  for (const item of items) {
    const issues = itemIssues(item);
    if (issues.length || item.disposition === 'block') {
      blockers.push(item.category);
      continue;
    }
    if (item.disposition === 'disclosed') {
      disclosures.push(item.summary);
    }
  }

  return {
    ready: blockers.length === 0,
    blockers,
    disclosures,
    readiness: {
      schemaVersion: 'release-readiness.v1',
      items,
    },
  };
}

export function normalizePostCloseActuals(value) {
  if (value == null) {
    return {
      schemaVersion: 'post-close-actuals.v1',
      status: 'not_started',
      periods: [],
      variance: [],
    };
  }
  if (typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('postCloseActuals must be an object');
  }
  const status = value.status;
  if (!['not_started', 'collecting', 'reconciled'].includes(status)) {
    throw new Error('postCloseActuals.status is invalid');
  }
  const periods = Array.isArray(value.periods) ? value.periods : null;
  const variance = Array.isArray(value.variance) ? value.variance : null;
  if (!periods || !variance) {
    throw new Error('postCloseActuals periods and variance must be arrays');
  }
  if (containsAbsolutePath(periods) || containsAbsolutePath(variance)) {
    throw new Error('postCloseActuals must not contain an absolute path');
  }
  return {
    schemaVersion: 'post-close-actuals.v1',
    status,
    periods,
    variance,
  };
}
